import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, ArrowUpRight, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Custom Software Development",
  "Web Development",
  "Mobile App Development",
  "AI / Machine Learning",
  "Data & Analytics",
  "Cloud Services",
  "IT Consulting",
  "Recruitment / Staffing",
  "Other",
];

const budgets = ["< $5k", "$5k – $15k", "$15k – $50k", "$50k – $150k", "$150k+", "Not sure yet"];

const timelines = ["ASAP", "1–4 weeks", "1–3 months", "3–6 months", "Flexible"];

export default function GetQuote() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service: "Custom Software Development",
    budget: "",
    timeline: "",
    message: "",
  });

  // Listen for global event from navbar, hero, etc.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("fis:open-quote", handler);
    return () => window.removeEventListener("fis:open-quote", handler);
  }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const pick = (k, v) => setForm({ ...form, [k]: v });

  const reset = () => {
    setForm({
      name: "",
      email: "",
      company: "",
      phone: "",
      service: "Custom Software Development",
      budget: "",
      timeline: "",
      message: "",
    });
    setStep(1);
  };

  const close = () => {
    setOpen(false);
    setTimeout(reset, 300);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and project details are required");
      setStep(2);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success("Quote request sent. We'll reply within 24 hours.");
      close();
    } catch {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* CTA band */}
      <section
        data-testid="get-quote-cta-band"
        className="bg-[color:var(--fis-accent)] text-white"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-8">
              <div className="font-mono-label text-white/70 mb-4">
                § Ready when you are
              </div>
              <h2 className="font-display text-5xl md:text-7xl leading-[0.95]">
                Have a project in mind?
                <br />
                <span className="text-white/70">Get a quote in 24h.</span>
              </h2>
              <p className="mt-6 max-w-xl text-white/80 leading-relaxed">
                Share the scope, budget and timeline — our team will reply with
                a fixed-fee proposal and a realistic delivery plan. No sales
                decks, no follow-up spam.
              </p>

              <div className="mt-10 flex flex-wrap gap-4 items-center">
                <button
                  onClick={() => setOpen(true)}
                  data-testid="get-quote-cta-button"
                  className="fis-btn bg-white text-[color:var(--fis-accent)] hover:bg-black hover:text-white text-base px-8 py-5 font-medium"
                >
                  <Zap className="w-5 h-5" />
                  Get a Quote
                  <ArrowUpRight className="w-5 h-5" />
                </button>
                <a
                  href="#contact"
                  data-testid="get-quote-contact-link"
                  className="text-white/80 underline underline-offset-4 hover:text-white text-sm"
                >
                  Prefer to email us? contact@frontlineintelligencesystem.com
                </a>
              </div>
            </div>

            <div className="md:col-span-4 hidden md:flex flex-col items-end gap-4 justify-center h-full min-h-[220px]">
              <div className="font-display text-7xl lg:text-9xl leading-none opacity-30">
                24h
              </div>
              <div className="font-mono-label text-white/60 tracking-[0.25em]">
                Avg. response time
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Floating mini-CTA that sits next to the chatbot isn't needed — keep CTA band only */}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            data-testid="quote-modal-overlay"
            className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm grid place-items-end md:place-items-center p-0 md:p-6"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full md:max-w-2xl md:max-h-[88vh] overflow-auto"
              data-testid="quote-modal"
            >
              <div className="flex items-start justify-between p-8 border-b fis-hairline">
                <div>
                  <div className="font-mono-label flex items-center gap-2">
                    <Sparkles className="w-3 h-3" /> Step {step} of 2
                  </div>
                  <h3 className="font-display text-3xl mt-2">
                    {step === 1 ? "Tell us about the project" : "And about you"}
                  </h3>
                </div>
                <button
                  onClick={close}
                  data-testid="quote-modal-close"
                  aria-label="Close"
                  className="p-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Step indicator */}
              <div className="flex h-1">
                <div
                  className={`flex-1 ${step >= 1 ? "bg-[color:var(--fis-accent)]" : "bg-black/10"}`}
                />
                <div
                  className={`flex-1 ${step >= 2 ? "bg-[color:var(--fis-accent)]" : "bg-black/10"}`}
                />
              </div>

              {step === 1 && (
                <div className="p-8 space-y-8" data-testid="quote-step-1">
                  <div>
                    <label className="font-mono-label block mb-3">
                      What do you need?
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {services.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => pick("service", s)}
                          data-testid={`quote-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className={`px-4 py-2 text-sm border transition ${
                            form.service === s
                              ? "bg-[color:var(--fis-ink)] text-white border-transparent"
                              : "border-black/15 hover:border-[color:var(--fis-ink)]"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="font-mono-label block mb-3">
                      Estimated budget
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {budgets.map((b) => (
                        <button
                          key={b}
                          type="button"
                          onClick={() => pick("budget", b)}
                          data-testid={`quote-budget-${b.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                          className={`px-4 py-2 text-sm border transition ${
                            form.budget === b
                              ? "bg-[color:var(--fis-accent)] text-white border-transparent"
                              : "border-black/15 hover:border-[color:var(--fis-accent)]"
                          }`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="font-mono-label block mb-3">Timeline</label>
                    <div className="flex flex-wrap gap-2">
                      {timelines.map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => pick("timeline", t)}
                          data-testid={`quote-timeline-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                          className={`px-4 py-2 text-sm border transition ${
                            form.timeline === t
                              ? "bg-[color:var(--fis-accent)] text-white border-transparent"
                              : "border-black/15 hover:border-[color:var(--fis-accent)]"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  <textarea
                    data-testid="quote-message"
                    className="fis-input resize-none"
                    rows={4}
                    placeholder="Brief description of what you're building or the problem to solve *"
                    value={form.message}
                    onChange={onChange("message")}
                  />

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        if (!form.message) {
                          toast.error("Add a few lines about the project");
                          return;
                        }
                        setStep(2);
                      }}
                      data-testid="quote-next"
                      className="fis-btn fis-btn-primary"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <form
                  onSubmit={submit}
                  className="p-8 space-y-6"
                  data-testid="quote-step-2"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <input
                      data-testid="quote-name"
                      className="fis-input"
                      placeholder="Full name *"
                      value={form.name}
                      onChange={onChange("name")}
                    />
                    <input
                      data-testid="quote-email"
                      className="fis-input"
                      placeholder="Work email *"
                      type="email"
                      value={form.email}
                      onChange={onChange("email")}
                    />
                    <input
                      data-testid="quote-company"
                      className="fis-input"
                      placeholder="Company"
                      value={form.company}
                      onChange={onChange("company")}
                    />
                    <input
                      data-testid="quote-phone"
                      className="fis-input"
                      placeholder="Phone"
                      value={form.phone}
                      onChange={onChange("phone")}
                    />
                  </div>

                  <div className="bg-[color:var(--fis-bg-alt)] p-5 text-sm">
                    <div className="font-mono-label mb-2">Summary</div>
                    <div>
                      <strong>{form.service}</strong>
                      {form.budget && <> · {form.budget}</>}
                      {form.timeline && <> · {form.timeline}</>}
                    </div>
                  </div>

                  <div className="flex justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      data-testid="quote-back"
                      className="fis-btn fis-btn-outline"
                    >
                      ← Back
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="quote-submit"
                      className="fis-btn fis-btn-accent disabled:opacity-60"
                    >
                      {submitting ? "Sending…" : "Send quote request"}
                      <ArrowUpRight className="w-4 h-4" />
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
